import { Badge } from '@/components/ui/badge';
import { CategoryBadge } from '@/components/ui/category-badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { MapPin, ExternalLink, Box, Image, Sparkles, ChevronDown, ChevronUp, ChevronRight, Bookmark, ScrollText, History, Info, Compass, Landmark } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { DiscoveryCard } from './discovery-card';
import aneTabletThumb from '@/assets/ane-tablet.png';

interface ArchaeologicalFindingCardProps {
    finding: ArchaeologicalFinding;
    bookName?: string;
    isExpanded: boolean;
    onToggleExpansion: () => void;
}

export function ArchaeologicalFindingCard({
    finding,
    bookName,
    isExpanded,
    onToggleExpansion,
}: ArchaeologicalFindingCardProps) {
    const isDarkMode = useDarkMode();

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            header={
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                        <div className="flex flex-col gap-1.5">
                            {/* Metadata Row */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                {finding.verseRef && (
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40">
                                        {bookName} {finding.verseRef}
                                    </span>
                                )}
                                {finding.artifactDate?.displayDate && (
                                    <>
                                        {finding.verseRef && <span className="w-1 h-1 rounded-full bg-primary/20" />}
                                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-1.5">
                                            <History className="h-2.5 w-2.5" />
                                            {finding.artifactDate.displayDate}
                                        </span>
                                    </>
                                )}
                            </div>

                            <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                                {finding.title}
                            </h4>
                        </div>

                        {finding.summary && <div className="space-y-3">
                            <p className={cn(
                                "text-sm text-muted-foreground leading-relaxed italic font-light",
                                !isExpanded && "line-clamp-3"
                            )}>
                                {finding.summary}
                            </p>
                        </div>}

                        {/* Integrated Discovery Buttons - Always visible in header for quick access */}
                        {(finding.imageSearchTerms || finding.virtualTourSearchTerms || finding.threeDModelSearchTerms) && (
                            <div className="flex flex-wrap gap-2 py-1">
                                {finding.imageSearchTerms && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={(e) => e.stopPropagation()}
                                        asChild
                                        className="h-7 px-3 rounded-lg transition-all duration-300 gap-1.5 hover:bg-primary/10 hover:text-primary border border-border/40 font-bold uppercase tracking-widest text-[8px]"
                                    >
                                        <a href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(finding.imageSearchTerms)}`} target="_blank" rel="noopener noreferrer">
                                            <Image className="h-3 w-3" />
                                            Evidence Photos
                                        </a>
                                    </Button>
                                )}
                                {finding.virtualTourSearchTerms && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={(e) => e.stopPropagation()}
                                        asChild
                                        className="h-7 px-3 rounded-lg transition-all duration-300 gap-1.5 hover:bg-primary/10 hover:text-primary border border-border/40 font-bold uppercase tracking-widest text-[8px]"
                                    >
                                        <a href={`https://www.google.com/search?q=${encodeURIComponent(finding.virtualTourSearchTerms)}`} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="h-3 w-3" />
                                            Virtual Visit
                                        </a>
                                    </Button>
                                )}
                                {finding.threeDModelSearchTerms && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={(e) => e.stopPropagation()}
                                        asChild
                                        className="h-7 px-3 rounded-lg transition-all duration-300 gap-1.5 hover:bg-primary/10 hover:text-primary border border-border/40 font-bold uppercase tracking-widest text-[8px]"
                                    >
                                        <a href={`https://www.google.com/search?q=${encodeURIComponent(finding.threeDModelSearchTerms)}`} target="_blank" rel="noopener noreferrer">
                                            <Box className="h-3 w-3" />
                                            3D Model
                                        </a>
                                    </Button>
                                )}
                            </div>
                        )}

                        <div className="space-y-3">
                            {finding.description && (
                                <div className={cn(
                                    "text-sm leading-relaxed text-foreground/75 font-light",
                                    !isExpanded && "line-clamp-3"
                                )}>
                                    {finding.description}
                                </div>
                            )}

                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                                    <Sparkles className="h-2.5 w-2.5" />
                                    <span>Discover More Details</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        >
            <div className="space-y-8 mt-6 relative z-10">
                {/* Biblical Illumination Layer */}
                {finding.relevance && (
                    <div className="space-y-3 px-2">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-blue-500/60" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-500/60">
                                Biblical Illumination
                            </span>
                        </div>
                        <p className={cn(
                            "text-sm leading-relaxed italic border-l-2 pl-4",
                            isDarkMode ? "text-blue-100/80 border-blue-500/20" : "text-blue-900 border-blue-200"
                        )}>
                            {finding.relevance}
                        </p>
                    </div>
                )}

                {/* Technical Dossier (Archive) */}
                <div className="relative pt-4 overflow-hidden rounded-2xl border border-border/10 bg-muted/5">
                    <button
                        onClick={onToggleExpansion}
                        className="flex items-center justify-between w-full px-5 py-3 group/archive text-left border-b border-border/10"
                    >
                        <div className="flex items-center gap-2">
                            <Landmark className="h-3.5 w-3.5 text-primary/40" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50">
                                Archeological Dossier
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-primary/40 group-hover/archive:text-primary/70 transition-colors">
                            {isExpanded ? "Close Archive" : "Open Archive"}
                            <ChevronDown className={cn("h-3 w-3 transition-transform", isExpanded && "rotate-180")} />
                        </div>
                    </button>

                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="p-5 space-y-8"
                            >
                                {/* Technical Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                    <div className="space-y-1.5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60">Site Detail</p>
                                        <p className="text-xs font-bold text-foreground">{finding.discovery.site || '—'}</p>
                                        <div className="text-[10px] text-muted-foreground/60 leading-tight flex flex-col gap-0.5">
                                            {finding.discovery.area && <span>Area: {finding.discovery.area}</span>}
                                            {finding.discovery.stratum && <span>Stratum: {finding.discovery.stratum}</span>}
                                            {finding.discovery.depth && <span>Depth: {finding.discovery.depth}</span>}
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60">Campaign</p>
                                        <p className="text-xs font-bold text-foreground">{finding.discovery.year || '—'}</p>
                                        <div className="text-[10px] text-muted-foreground/60 leading-tight flex flex-col gap-0.5">
                                            {finding.discovery.excavator && <span className="italic">{finding.discovery.excavator}</span>}
                                            {finding.discovery.season && <span>{finding.discovery.season}</span>}
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60">Publication</p>
                                        <p className="text-xs italic text-muted-foreground leading-snug">{finding.discovery.publication || '—'}</p>
                                    </div>
                                </div>

                                {/* Discovery Context */}
                                {finding.discovery.context && (
                                    <div className="space-y-2 pt-4 border-t border-border/5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60">Discovery Context</p>
                                        <p className="text-xs text-muted-foreground/80 leading-relaxed">{finding.discovery.context}</p>
                                    </div>
                                )}

                                {/* Museum/Site Hint */}
                                {finding.museumOrSiteHint && (
                                    <div className="space-y-2 pt-4 border-t border-border/5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60">Museum/Site</p>
                                        <p className="text-xs text-muted-foreground/80 leading-relaxed">{finding.museumOrSiteHint}</p>
                                    </div>
                                )}

                                {/* Dating Context */}
                                <div className="space-y-3 pt-4 border-t border-border/5">
                                    <div className="flex items-center gap-3">
                                        <Badge variant="outline" className="px-2 py-0.5 rounded-lg border-primary/20 text-primary bg-primary/5 text-[9px] font-bold uppercase tracking-widest">
                                            {finding.artifactDate.displayDate}
                                        </Badge>
                                        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/30">{finding.artifactDate.chronology} Anchor</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground/80 italic leading-relaxed">
                                        {finding.artifactDate.rationale}
                                    </p>
                                </div>

                                {/* Alternative Dates */}
                                {finding.alternativeDates && finding.alternativeDates.length > 0 && (
                                    <div className="space-y-3 pt-4 border-t border-border/5">
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary/60">Alternative Dating Proposals</p>
                                        <div className="space-y-3">
                                            {finding.alternativeDates.map((altDate, index) => (
                                                <div key={index} className="space-y-1.5">
                                                    <div className="flex items-center gap-3">
                                                        <Badge variant="outline" className="px-2 py-0.5 rounded-lg border-muted-foreground/20 text-muted-foreground bg-muted/20 text-[9px] font-bold uppercase tracking-widest">
                                                            {altDate.displayDate}
                                                        </Badge>
                                                        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/30">{altDate.chronology}</span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground/70 italic leading-relaxed pl-2">
                                                        {altDate.rationale}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer Metadata */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-border/10">
                    <div className="flex items-center gap-3 flex-wrap">
                        {finding.importance && (
                            <ImportanceBadge
                                importance={finding.importance}
                                context="archaeology"
                                className="h-4 text-[9px] px-1.5"
                            />
                        )}
                        {finding.confidence && (
                            <ConfidenceBadge confidence={finding.confidence} context="general" className="h-4 text-[9px]" />
                        )}
                        {finding.categories?.map((category) => (
                            <CategoryBadge key={category} category={category} className="h-4 text-[9px]" />
                        ))}
                    </div>

                    {finding.sources?.length > 0 && (
                        <SourceLinks sources={finding.sources} />
                    )}
                </div>
            </div>
        </DiscoveryCard>
    );
}
